import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkFeatureAllowedByName,
  FeatureDefinitionName,
} from '@main/access-control-types';
import type { ApolloError } from '@main/core-ui';
import type { PrivacyCenter } from '@main/pc-types';
import { parsePrivacyCenter, PrivacyCenterModule } from '@main/pc-types';

import { buildUseCdnQueryWithFallback } from '../../hooks';
import { appendStylesheet, changeFavicon } from './helpers';
import { selectPrivacyCenter } from './selectors';
import { clearPrivacyCenter, setPrivacyCenter } from './slice';

const usePrivacyCenter = buildUseCdnQueryWithFallback(
  PrivacyCenterModule.PrivacyCenter,
);

/**
 * Hook to fetch privacy center metadata
 *
 * @returns The privacy center metadata
 */
export const usePrivacyCenterData = (): {
  /** Loading state */
  loading: boolean;
  /** Error if ocurred */
  error?: ApolloError;
  /** Parsed privacy center */
  privacyCenter?: PrivacyCenter;
  /** Whether privacy center is disabled */
  isDisabled: boolean;
} => {
  const { data, loading, error } = usePrivacyCenter();
  const privacyCenter = useSelector(selectPrivacyCenter);
  const dispatch = useDispatch();

  // Set the fonts
  useEffect(() => {
    if (privacyCenter) {
      const {
        assets: { favicon },
        theme,
      } = privacyCenter;

      // Set favicon
      if (favicon) {
        changeFavicon(favicon.src);
      }

      // Set fonts
      if (theme.textStyles.fontFamilyHeader) {
        appendStylesheet(theme.textStyles.fontFamilyHeader.url);
      }
      if (theme.textStyles.fontFamilyBody) {
        appendStylesheet(theme.textStyles.fontFamilyBody.url);
      }
    }
  }, [privacyCenter]);

  // Forward the privacy center to the redux store so that it is accessible
  // throughout the site
  useEffect(() => {
    if (data) {
      const parsedPrivacyCenter = parsePrivacyCenter(data);

      if (parsedPrivacyCenter.isDisabled) {
        dispatch(clearPrivacyCenter());
      } else {
        const { features, featureSet } = parsedPrivacyCenter.organization;
        const hasDeprecatedFeatureSet = features === undefined;

        const disabledPrivacyCenterAboutTranscend = hasDeprecatedFeatureSet
          ? featureSet.disabledPrivacyCenterAboutTranscend
          : checkFeatureAllowedByName(
              FeatureDefinitionName.DisabledPrivacyCenterAboutTranscend,
              features,
            );

        const whiteLabelPrivacyCenter = hasDeprecatedFeatureSet
          ? featureSet.whiteLabelPrivacyCenter
          : checkFeatureAllowedByName(
              FeatureDefinitionName.WhiteLabelPrivacyCenter,
              features,
            );

        const workflowsV2Enabled = hasDeprecatedFeatureSet
          ? featureSet.workflowsv2
          : checkFeatureAllowedByName(
              FeatureDefinitionName.WorkflowsV2,
              features,
            );

        dispatch(
          setPrivacyCenter({
            privacyCenter: parsedPrivacyCenter,
            sombraPublicKey: data.organization.sombraPublicKeyECDH!,
            whiteLabelPrivacyCenter,
            disabledPrivacyCenterAboutTranscend,
            workflowsV2Enabled,
          }),
        );
      }
    }
  }, [data, dispatch]);

  return {
    loading,
    error,
    privacyCenter,
    isDisabled: !!data?.isDisabled,
  };
};
